import { createDecorator } from 'vue-class-component';
import { throttle } from '@/design-system/utilities/Throttle';
import { Vue } from 'vue/types/vue';

/*
 * This method finds and replaces methods found in a component's internal watcher list
 * with its throttled counterpart.
 */
function replaceWatcherHooks(
    this: Vue & VueInternalWatcher,
    originalMethod: Function,
    throttledMethod: Function,
) {
    this._watchers?.filter(watcher => watcher.cb === originalMethod).forEach((watcher) => {
        watcher.cb = throttledMethod;
    });
}

// Disabling this rule here because the helper function to be below the main export is more
// navigatable for the uninitiated. Because `replaceWatcherHooks` is not the main functionality of
// this file.
export default (wait?: number, options = {}) =>
    createDecorator((componentOptions, key) => {
        // Get the ref to the original `created` hook to call it later. Because we do not want to
        // replace the hook completely.
        const originalCreatedHook = componentOptions.created;

        componentOptions.created = function () {
            // Call the original hook.
            if (originalCreatedHook) {
                originalCreatedHook.call(this);
            }

            // @ts-ignore: Suppress index type error
            const originalMethod = this[key];

            // Change the component method with its throttled counterpart.
            // @ts-ignore: Suppress index type error
            this[key] = throttle(originalMethod, wait, options);

            // Change any watchers with the new throttled method. Because watchers are _mostly_
            // defined on the component definition, rather than after the component is initialized.
            // So the throttling that is applied after the initialization is not taken into effect
            // for watchers, and we need to do it manually.
            // @ts-ignore: Suppress errors regarding Vue & VueInternalWatcher and index type
            replaceWatcherHooks.call(this, originalMethod, this[key]);
        };
    });

type VueInternalWatcher = {
    _watchers: Array<{ cb: Function }>
}
